import type { FeaturePageData, ICPPageData, StarterPipeline } from "./types";

export const STARTERS: Record<string, StarterPipeline> = {
  "auto-caption": {
    id: "auto-caption",
    name: "Auto-Caption",
    type: "VIDEO",
    nodes: 4,
    description: "Transcribe, burn subtitles, deliver.",
  },
  "social-clips": {
    id: "social-clips",
    name: "Social Clips",
    type: "VIDEO",
    nodes: 6,
    description: "Turn one video into platform-specific variants.",
  },
  thumbnails: {
    id: "thumbnails",
    name: "Thumbnails",
    type: "VIDEO",
    nodes: 3,
    description: "Generate engaging thumbnails from video frames.",
  },
  "color-grade": {
    id: "color-grade",
    name: "Color Grade",
    type: "VIDEO",
    nodes: 5,
    description: "Apply LUTs and color corrections automatically.",
  },
  "batch-resize": {
    id: "batch-resize",
    name: "Batch Resize",
    type: "IMAGE",
    nodes: 2,
    description: "Process images to multiple specs.",
  },
  "image-gen": {
    id: "image-gen",
    name: "Image Gen",
    type: "IMAGE",
    nodes: 3,
    description: "Generate assets with AI and approval gates.",
  },
};

export const FEATURES_DATA: Record<string, FeaturePageData> = {
  runtime: {
    title: "Runtime",
    oneLiner: "Cloud processing that scales with your workload.",
    capabilities: [
      {
        title: "Auto-scaling Media Engines",
        description: "Spawns workers on demand. Pay only for what you use.",
      },
      { title: "Watch Folders", description: "Drop files in, pipeline triggers automatically." },
      { title: "Cost Tracking", description: "Know the cost of every run before and after." },
      { title: "Delivery Rules", description: "Auto-sync outputs to any destination." },
    ],
    relatedStarters: ["auto-caption", "social-clips", "batch-resize"],
  },
  gates: {
    title: "Gates",
    oneLiner: "Human checkpoints in automated pipelines.",
    capabilities: [
      { title: "Approval Gates", description: "Pause for human review, then auto-continue." },
      { title: "Remote Gates", description: "Approve from Frame.io, email, or Slack." },
      { title: "Conditional Gates", description: "Skip approval when confidence is high." },
      { title: "Notifications", description: "Get notified when a gate needs attention." },
      { title: "Gate Notes", description: "Discuss, @mention, and annotate before resuming." },
    ],
    relatedStarters: ["auto-caption", "image-gen"],
  },
  composition: {
    title: "Composition",
    oneLiner: "Build pipelines from modular nodes.",
    capabilities: [
      { title: "Node Library", description: "50+ built-in nodes for AI, Video Tools, and integrations." },
      { title: "Starter pipelines", description: "Start from proven pipelines, customize as needed." },
      { title: "Modules", description: "Reuse published building blocks via sub-pipeline nodes." },
      { title: "Custom Nodes", description: "Build your own with Python or JavaScript." },
      { title: "Connections", description: "Connect to S3, GCS, Dropbox, Frame.io, and more." },
    ],
    relatedStarters: ["social-clips", "color-grade", "thumbnails"],
  },
  observability: {
    title: "Observability",
    oneLiner: "See everything that happens in your pipelines.",
    capabilities: [
      { title: "Activity Feed", description: "Real-time view of all pipeline runs." },
      { title: "Run History", description: "Full history with inputs, outputs, and costs." },
      { title: "Debug Mode", description: "Step through failed runs to find issues." },
      { title: "Alerts", description: "Get notified on failures or anomalies." },
    ],
    relatedStarters: ["auto-caption", "batch-resize"],
  },
  collaboration: {
    title: "Collaboration",
    oneLiner: "Work together on pipelines.",
    capabilities: [
      { title: "Organizations", description: "Top-level container for billing and members." },
      { title: "Teams", description: "Organize members into teams with separate projects." },
      { title: "Permissions", description: "Control who can edit, run, or view pipelines." },
      { title: "Shared Library", description: "Reusable assets across your organization." },
      { title: "Gate Notes", description: "Coordinate on reviews with comments, mentions, and annotations." },
    ],
    relatedStarters: ["social-clips", "image-gen"],
  },
};

export const ICP_DATA: Record<string, ICPPageData> = {
  agencies: {
    role: "Agencies",
    headline: "Scale client deliverables without scaling headcount.",
    painPoints: [
      "Client wants 20 versions → manual processing nightmare",
      "Inconsistent output quality across projects",
      "Can't track costs per client accurately",
    ],
    solutions: [
      "Batch process all variants in one pipeline run",
      "Starter pipelines help teams standardize faster",
      "Cost tracking shows spend per run, per client",
    ],
    starter: "social-clips",
  },
  studios: {
    role: "Studios",
    headline: "Automate repetitive post-production.",
    painPoints: [
      "Manual transcoding for every deliverable",
      "QC bottleneck before final delivery",
      "Talented editors stuck on repetitive tasks",
    ],
    solutions: [
      "Auto-transcode to all delivery specs",
      "Gates pause for QC, then auto-deliver",
      "Free up editors for creative work",
    ],
    starter: "auto-caption",
  },
  marketing: {
    role: "Marketing",
    headline: "Turn one video into 20 social clips.",
    painPoints: ["Video team is the bottleneck", "Can't repurpose content fast enough", "Social platforms need different formats"],
    solutions: ["AI finds the best clips automatically", "Resize and reformat for each platform", "Publish directly to your CMS"],
    starter: "social-clips",
  },
  ecommerce: {
    role: "E-commerce",
    headline: "Batch process product photos at scale.",
    painPoints: [
      "1000 product photos need consistent treatment",
      "Manual editing doesn't scale with catalog growth",
      "Different marketplaces need different specs",
    ],
    solutions: ["Drop folder of images, get processed outputs", "Modules ensure consistent quality every time", "Auto-resize for Amazon, Shopify, etc."],
    starter: "batch-resize",
  },
};

